import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../lib/AuthContext";
import useBrandTranslation from "../../hooks/useBrandTranslation";
import useInventorySettings from "../../hooks/useInventorySettings";
import { saveInventorySettings } from "../../apis/api.inventory";
import { queryInventorySettingsKey } from "../../apis/queryKeys";
import { WORLD_CURRENCIES, currencyLabel } from "../../utils/currencies";
import { parseAmount, formatAmount } from "../../utils/inventoryFormat";
import customToast from "../common/Toaster";
import Button from "../common/Button";
import { Input } from "../common/Input";
import { Label } from "../common/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../common/Select";
import Spinner from "../common/Spinner";
import { Coins } from "lucide-react";

// Configuración de "Monedas" del owner (profesional o institución). Vive en Perfil → Monedas.
export default function MonedasTab() {
  const { t } = useBrandTranslation();
  const { user } = useAuth();
  const ownerId = user?.id;
  const queryClient = useQueryClient();

  const { settings, decimalSeparator, isLoading } = useInventorySettings(ownerId);

  const [defaultCurrency, setDefaultCurrency] = useState("");
  const [separator, setSeparator] = useState(",");
  const [altCurrency, setAltCurrency] = useState("");
  const [exchangeRate, setExchangeRate] = useState("");

  useEffect(() => {
    if (!settings) return;
    setDefaultCurrency(settings.default_currency || "");
    setSeparator(settings.decimal_separator === "." ? "." : ",");
    setAltCurrency(settings.alt_currency || "");
    setExchangeRate(
      settings.exchange_rate != null
        ? formatAmount(settings.exchange_rate, settings.decimal_separator || ",")
        : ""
    );
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: (fields) => saveInventorySettings(ownerId, fields),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryInventorySettingsKey(ownerId) });
      customToast(t("inventory.currencies.saved", "Configuración de monedas guardada"), "success");
    },
    onError: (e) =>
      customToast(
        e?.message || t("inventory.currencies.saveError", "Error al guardar la configuración"),
        "danger"
      ),
  });

  const handleSave = () => {
    saveMutation.mutate({
      default_currency: defaultCurrency || null,
      decimal_separator: separator,
      alt_currency: altCurrency || null,
      exchange_rate: parseAmount(exchangeRate, separator),
    });
  };

  const lastUpdate = settings?.exchange_rate_updated_at
    ? new Date(settings.exchange_rate_updated_at).toLocaleString()
    : t("inventory.currencies.notUpdatedYet", "Sin actualizar");

  if (isLoading) return <Spinner className="w-8 h-8 mx-auto my-8" />;

  const labelClass = "text-sm font-semibold text-gray-800";

  return (
    <div className="space-y-8 px-1 pt-4 pb-24 max-w-xl mx-auto">
      <div className="flex items-center gap-2">
        <Coins className="w-6 h-6 text-amber-500" />
        <h2 className="text-xl font-bold text-gray-900">
          {t("inventory.currencies.title", "Monedas")}
        </h2>
      </div>

      {/* Moneda predeterminada */}
      <section className="rounded-lg border border-gray-200 p-4 space-y-3">
        <div>
          <Label className={labelClass}>
            {t("inventory.currencies.defaultTitle", "Moneda predeterminada")}
          </Label>
          <p className="text-xs text-gray-500 italic">
            {t("inventory.currencies.defaultDescription", "ésta es la moneda principal")}
          </p>
        </div>
        <Select value={defaultCurrency} onValueChange={setDefaultCurrency}>
          <SelectTrigger>
            <SelectValue
              placeholder={t("inventory.currencies.selectCurrency", "Seleccioná una moneda")}
            />
          </SelectTrigger>
          <SelectContent>
            {WORLD_CURRENCIES.map((c) => (
              <SelectItem key={c.code} value={c.code}>
                {currencyLabel(c)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="pt-2">
          <Label className={labelClass}>
            {t(
              "inventory.currencies.decimalQuestion",
              "¿Cómo deseas que se vean las posiciones decimales?"
            )}
          </Label>
          <Select value={separator} onValueChange={setSeparator}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value=",">
                {t("inventory.currencies.decimalComma", "Coma ( , )")}
              </SelectItem>
              <SelectItem value=".">
                {t("inventory.currencies.decimalDot", "Punto ( . )")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </section>

      {/* Moneda alternativa */}
      <section className="rounded-lg border border-gray-200 p-4 space-y-3">
        <div>
          <Label className={labelClass}>
            {t("inventory.currencies.altTitle", "Moneda alternativa")}
          </Label>
          <p className="text-xs text-gray-500 italic">
            {t(
              "inventory.currencies.altDescription",
              "moneda de conversión, para aquellos insumos que lo posean"
            )}
          </p>
        </div>
        <Select value={altCurrency} onValueChange={setAltCurrency}>
          <SelectTrigger>
            <SelectValue
              placeholder={t("inventory.currencies.selectCurrency", "Seleccioná una moneda")}
            />
          </SelectTrigger>
          <SelectContent>
            {WORLD_CURRENCIES.map((c) => (
              <SelectItem key={c.code} value={c.code}>
                {currencyLabel(c)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="pt-2">
          <Label className={labelClass}>
            {t("inventory.currencies.exchangeRate", "Tipo de cambio")}
          </Label>
          <Input
            className="mt-1"
            inputMode="decimal"
            value={exchangeRate}
            onChange={(e) => setExchangeRate(e.target.value)}
            placeholder={t("inventory.currencies.exchangeRatePlaceholder", "Ingresá el valor")}
          />
        </div>

        <div className="pt-1">
          <Label className={labelClass}>
            {t("inventory.currencies.lastUpdate", "Última Actualización")}
          </Label>
          <p className="text-sm text-gray-600">{lastUpdate}</p>
        </div>

        <p className="text-xs text-gray-500 bg-amber-50 border border-amber-200 rounded-md p-3 leading-relaxed">
          {t(
            "inventory.currencies.exchangeHint",
            "El valor que ingreses será multiplicado por la moneda predeterminada..."
          )}
        </p>
      </section>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saveMutation.isPending}>
          {saveMutation.isPending
            ? t("inventory.common.saving", "Guardando…")
            : t("inventory.currencies.save", "Guardar")}
        </Button>
      </div>
    </div>
  );
}
